import React from 'react';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';

export interface CustomerScreenItem {
  id: string;
  label: string;
}

interface CustomerInfo {
  name: string;
  logoPath: string;
  color: string;
  backgroundColor: string;
}

interface CustomerTopNavProps {
  customerInfo: CustomerInfo;
  screenConfig: CustomerScreenItem[];
  currentScreenIndex: number;
  onNavigateHome: () => void;
  onNavigateScreen: (screenIndex: number) => void;
}

export const CustomerTopNav: React.FC<CustomerTopNavProps> = ({
  customerInfo,
  screenConfig,
  currentScreenIndex,
  onNavigateHome,
  onNavigateScreen,
}) => {
  const navScreens = screenConfig.filter((s) => s.id !== 'home');
  const currentId = screenConfig[currentScreenIndex]?.id;
  const navTabIndex = navScreens.findIndex((s) => s.id === currentId);

  return (
    <Box
      sx={{
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        bgcolor: '#ffffff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      <Box
        sx={{
          bgcolor: customerInfo.backgroundColor,
          borderBottom: 1,
          borderColor: customerInfo.backgroundColor === '#f3f709' ? '#e3e8ef' : 'transparent',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <img
            src={customerInfo.logoPath}
            alt={customerInfo.name}
            style={{
              width: 80,
              height: 65,
              objectFit: 'contain',
              borderRadius: 4,
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              color: customerInfo.color,
              fontSize: { xs: 16, sm: 18 },
            }}
          >
            {customerInfo.name}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={onNavigateHome}
          sx={{
            borderColor: customerInfo.color,
            color: customerInfo.color,
            fontWeight: 600,
            px: 2,
            py: 0.5,
            borderRadius: 2,
            fontSize: { xs: 12, sm: 14 },
            '&:hover': {
              borderColor: customerInfo.color,
              bgcolor: `${customerInfo.color}10`,
            },
          }}
        >
          홈
        </Button>
      </Box>

      <Box
        sx={{
          bgcolor: '#ffffff',
          borderBottom: '1px solid #e3e8ef',
          px: 2,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Tabs
          value={navTabIndex >= 0 ? navTabIndex : false}
          onChange={(_e, newValue: number) => {
            const target = navScreens[newValue];
            if (!target) return;
            const idx = screenConfig.findIndex((s) => s.id === target.id);
            if (idx >= 0) onNavigateScreen(idx);
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            justifyContent: 'flex-end',
            width: 'auto',
            '& .MuiTabs-flexContainer': {
              justifyContent: 'flex-end',
            },
            '& .MuiTab-root': {
              color: '#666',
              fontWeight: 600,
              fontSize: { xs: 14, sm: 16 },
              minWidth: 'auto',
              px: 2,
              py: 1.5,
              textTransform: 'none',
              minHeight: 'auto',
              '&.Mui-selected': {
                color: customerInfo.color,
                fontWeight: 700,
              },
            },
            '& .MuiTabs-indicator': {
              bgcolor: customerInfo.color,
              height: 3,
            },
          }}
        >
          {navScreens.map((item) => (
            <Tab key={item.id} label={item.label} />
          ))}
        </Tabs>
      </Box>
    </Box>
  );
};
